import React from "react";
import BasicFormElement from "modules/forms/hosted/formElements/BasicFormElement";
import useFormFlow from "modules/forms/hooks/useFormFlow";
import Select from "modules/forms/hosted/formElements/Select";
import MultiSelect from "modules/forms/hosted/formElements/MultiSelect";
import Toggle from "modules/forms/hosted/formElements/Toggle";
import TextArea from "modules/forms/hosted/formElements/TextArea";
import { FormBlockType } from "modules/forms/hooks/usefetchForm";
import { Option } from "modules/forms/hosted/formElements/Combobox";
import Range from "modules/forms/hosted/formElements/Range";
import { FormElementType } from "modules/forms/hooks/useFormElement";
import Checkbox from "modules/forms/hosted/formElements/Checkbox";
import BlockWrapper from "modules/forms/hosted/blocks/BlockWrapper";

export type CustomFieldBlockType = Omit<FormBlockType, "data"> & {
    data: {
        label: string;
        hide_label: boolean;
        hidden?: boolean;
        default_value: any;
        options: Option[];
        type: FormElementType & "switch" & "slider"; // The switch is actually old. Now it is toggle.
    },
}

export default function CustomField(block: CustomFieldBlockType) {
    const { values, onChange } = useFormFlow();
    const elementType = block.data.type;

    if (elementType == FormElementType.RANGE || elementType == "slider") {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <Range
                        {...block.data}
                        id={block.uuid}
                        name={block.name}
                        onChange={e => onChange(block.name, e.target.value)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.DATE) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <BasicFormElement
                        {...block.data}
                        type={"date"}
                        id={block.uuid}
                        value={values[block.name] ?? ""}
                        onChange={e => onChange(block.name, e.target.value)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.SELECT) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <Select id={block.uuid} {...block.data} options={block.data.options ?? []} name={block.name}/>
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.MULTI_SELECT) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <MultiSelect id={block.uuid} {...block.data} options={block.data.options ?? []} name={block.name}/>
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.DATE_TIME) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <BasicFormElement
                        {...block.data}
                        type={"datetime-local"}
                        id={block.uuid}
                        value={values[block.name] ?? ""}
                        onChange={e => onChange(block.name, e.target.value)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.TOGGLE || elementType == "switch" || elementType == "boolean") {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-6"}>
                    <Toggle
                        {...block.data}
                        id={block.uuid}
                        checked={values[block.uuid]}
                        onChange={e => onChange(block.name, e.target.checked)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.CHECKBOX) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-6"}>
                    <Checkbox
                        {...block.data}
                        label={block.data.label}
                        checked={values[block.uuid]}
                        onChange={checked => onChange(block.name, checked)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.TEXT_AREA) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <TextArea
                    {...block.data}
                    id={block.uuid}
                    value={values[block.name] ?? ""}
                    onChange={text => onChange(block.name, text)}
                />
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.NUMBER) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <BasicFormElement
                        {...block.data}
                        id={block.uuid}
                        value={values[block.name] ?? ""}
                        type={"number"}
                        step={0.01}
                        onChange={e => onChange(block.name, e.target.value)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    if (elementType == FormElementType.TEXT) {
        return (
            <BlockWrapper hidden={block.data.hidden}>
                <div className={"my-4"}>
                    <BasicFormElement
                        {...block.data}
                        id={block.uuid}
                        value={values[block.name] ?? ""}
                        type={"text"}
                        onChange={e => onChange(block.name, e.target.value)}
                    />
                </div>
            </BlockWrapper>
        );
    }

    return (
        <BlockWrapper hidden={block.data.hidden}>
            <div className={"my-4"}>
                <BasicFormElement
                    {...block.data}
                    id={block.uuid}
                    value={values[block.name] ?? ""}
                    type={elementType}
                    onChange={e => onChange(block.name, e.target.value)}
                />
            </div>
        </BlockWrapper>
    );
}
